import React, { useState, useMemo } from 'react';
import { Calculator, DollarSign, TrendingUp, Rocket, Crown, ChevronDown } from 'lucide-react';

export const PlanCalculator = () => {
  const [launchCost, setLaunchCost] = useState(50);
  const [growthCost, setGrowthCost] = useState(75);
  const [premiumCost, setPremiumCost] = useState(94);
  const [appsSold, setAppsSold] = useState(10);

  const LAUNCH_PRICE = 99;
  const GROWTH_PRICE = 199;
  const PREMIUM_PRICE = 299;

  const calc = useMemo(() => {
    const make = (price, cost) => {
      const gross = appsSold * price;
      const totalCost = appsSold * cost;
      const net = gross - totalCost;
      return { gross, totalCost, net, arr: net * 12, costArr: totalCost * 12, margin: gross > 0 ? ((net / gross) * 100).toFixed(1) : '0' };
    };
    return { launch: make(LAUNCH_PRICE, launchCost), growth: make(GROWTH_PRICE, growthCost), premium: make(PREMIUM_PRICE, premiumCost) };
  }, [launchCost, growthCost, premiumCost, appsSold]);

  const fmt = (n) => n < 0 ? `-$${Math.abs(n).toLocaleString()}` : `$${n.toLocaleString()}`;

  const PlanCol = ({ label, icon, price, color, data, cost, setCost }) => (
    <div data-testid={`calc-${label.toLowerCase()}`} style={{ background: '#111', border: `2px solid ${color}`, borderRadius: '10px', padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {icon}
        <span style={{ fontFamily: "'Bangers', cursive", fontSize: '1.1rem', color, letterSpacing: '0.03em' }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontFamily: "'Bangers', cursive", fontSize: '0.9rem', color: '#666' }}>${price}/mo</span>
      </div>
      {/* Cost input */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 700 }}>Cost/app $</span>
        <input type="number" min={0} step={1} value={cost} onChange={e => setCost(Number(e.target.value) || 0)}
          style={{ width: '60px', padding: '4px 6px', background: '#0a0a0a', border: `1.5px solid ${color}40`, borderRadius: '4px', color, fontSize: '0.9rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <Row label={`Revenue (${appsSold}×$${price})`} value={fmt(data.gross)} color={color} />
        <Row label={`Cost (${appsSold}×$${cost})`} value={`-${fmt(data.totalCost)}`} color="#FF3B30" />
        <Row label="Cost ARR" value={`-${fmt(data.costArr)}`} color="#FF3B30" />
        <div style={{ height: '1.5px', background: color, margin: '0.15rem 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.6rem', background: data.net >= 0 ? `${color}15` : '#FF3B3015', border: `1.5px solid ${data.net >= 0 ? color : '#FF3B30'}`, borderRadius: '5px' }}>
          <span style={{ fontWeight: 800, fontSize: '0.7rem', color: '#fff', textTransform: 'uppercase' }}>Net/mo</span>
          <span style={{ fontFamily: "'Bangers', cursive", fontSize: '1.4rem', color: data.net >= 0 ? color : '#FF3B30' }}>{fmt(data.net)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.6rem', background: '#0a0a0a', border: `1.5px solid ${data.arr >= 0 ? color : '#FF3B30'}40`, borderRadius: '5px' }}>
          <span style={{ fontWeight: 800, fontSize: '0.7rem', color: '#FFD700', textTransform: 'uppercase' }}>ARR</span>
          <span style={{ fontFamily: "'Bangers', cursive", fontSize: '1.2rem', color: data.arr >= 0 ? '#FFD700' : '#FF3B30' }}>{fmt(data.arr)}</span>
        </div>
        <div style={{ textAlign: 'center', padding: '0.2rem', fontSize: '0.7rem' }}>
          <span style={{ color: '#888', fontWeight: 700 }}>Margin </span>
          <span style={{ fontWeight: 800, color: data.net >= 0 ? color : '#FF3B30' }}>{data.margin}%</span>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="plan-calculator" style={{ background: '#0a0a0a', borderTop: '2px solid #222', padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <Calculator size={18} style={{ color: '#FFD700' }} />
        <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.2rem', color: '#FFD700', letterSpacing: '0.04em', margin: 0 }}>Plan Profit Calculator</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', fontWeight: 700 }}>Apps Sold</span>
          <input data-testid="calc-apps-input" type="number" min={1} step={1} value={appsSold}
            onChange={e => setAppsSold(Number(e.target.value) || 1)}
            style={{ width: '70px', padding: '5px 6px', background: '#111', border: '2px solid #FFD700', borderRadius: '6px', color: '#FFD700', fontSize: '1rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }} />
          <input type="range" min={1} max={100} step={1} value={appsSold}
            onChange={e => setAppsSold(Number(e.target.value))}
            style={{ width: '120px', height: '6px', borderRadius: '3px', appearance: 'none', background: `linear-gradient(to right, #FFD700 0%, #FFD700 ${((appsSold - 1) / 99) * 100}%, #333 ${((appsSold - 1) / 99) * 100}%, #333 100%)`, cursor: 'pointer' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
        <PlanCol label="Launch" icon={<Rocket size={16} color="#22c55e" />} price={LAUNCH_PRICE} color="#22c55e" data={calc.launch} cost={launchCost} setCost={setLaunchCost} />
        <PlanCol label="Growth" icon={<TrendingUp size={16} color="#FF8C00" />} price={GROWTH_PRICE} color="#FF8C00" data={calc.growth} cost={growthCost} setCost={setGrowthCost} />
        <PlanCol label="Premium" icon={<Crown size={16} color="#FF3B30" />} price={PREMIUM_PRICE} color="#FF3B30" data={calc.premium} cost={premiumCost} setCost={setPremiumCost} />
      </div>
    </div>
  );
};

const Row = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0.6rem', background: '#0a0a0a', borderRadius: '5px' }}>
    <span style={{ color: '#888', fontWeight: 600, fontSize: '0.7rem' }}>{label}</span>
    <span style={{ fontFamily: "'Bangers', cursive", fontSize: '1rem', color }}>{value}</span>
  </div>
);
